import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SpinnerService } from '@shared/services/spinner.service';
import { Ticket } from '@routes/ticket-manager/ticket';
import { AuthService } from '@core/auth/auth.service';
import { ApiService } from '@shared/apis';
import { EventService } from '@shared/services/event.service';
import { MasterCode } from '@shared/enums/master-code';
import { Constants } from '@shared/constants/constants';
import { PusherService } from '@shared/services/pusher.service';
import { PusherEvent } from '@shared/enums/pusher-event';
import { finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TicketManager {

  tickets: Ticket[] = [];
  newTicketNum = 0;
  public activeTicket$: BehaviorSubject<any> = new BehaviorSubject(null);
  lookupChanged$ = new BehaviorSubject<any>(null);
  headerTabChanged$ = new BehaviorSubject<any>(null);
  subscription: any = {};
  company;
  // region pusher handler
  private pusherChannels: any = {
    ticketChannel: null,
  }

  constructor(private authService: AuthService,
              private apiService: ApiService,
              private eventService: EventService,
              private pusherService: PusherService,
              private spinnerService: SpinnerService) {
    this.company = this.authService.getCurrentCompany();
  }

  private _activeTicket: Ticket;

  public get activeTicket() {
    return this._activeTicket;
  }

  // <editor-folder defaultstate="collapsed" desc="ticket">
  public set activeTicket(ticket: Ticket) {
    if (this.activeTicket?.ticketInfo.ticketNo === ticket?.ticketInfo.ticketNo) {
      return;
    }

    this._activeTicket = ticket;
    this.activeTicket$.next(this._activeTicket);
  }

  public set updateTicket(ticket: Ticket) {
    this._activeTicket = ticket;
    this.activeTicket$.next(this._activeTicket);
  }

  // </editor-folder>

  // <editor-folder defaultstate="collapsed" desc="start/end">
  async start() {
    await this.loadCommon();
  }

  addTicket(ticketInfo?: any, publish = true) {
    const { ticketNo, reload, status } = ticketInfo || {};

    const loadTicket = this.tickets.find(f => f.ticketInfo && f.ticketInfo?.ticketNo === ticketNo);
    if (loadTicket) {
      loadTicket.active = true;
      loadTicket.load(loadTicket);

      this.activeTicket = loadTicket;
      return;
    }

    const ticket = new Ticket(this.apiService, this.authService, this.spinnerService);

    const addTicketInfo: any = {};
    let newObj: any = {};
    if (publish) {
      this.newTicketNum = reload ? ticketNo : parseInt((ticketNo > 1 && status === 'new') ? ticketNo : this.newTicketNum) + 1;
      newObj = {
        ticketNo: this.newTicketNum,
        status: 'new',
      };
      if (status === 'product') {
        newObj.tempProduct = ticketInfo.tempProduct;
        ticketInfo = null;
      }
    }
    addTicketInfo.ticketInfo = ticketInfo || newObj;
    addTicketInfo.active = true;

    ticket.load(addTicketInfo, publish);
    this.tickets.unshift(ticket);
    this.subscribePusher(ticket);
    this.activeTicket = ticket;
  }

  changeTicket(ticket: Ticket) {
    const { ticketInfo } = ticket;

    this.eventService.closeTab$.next({
      ticketNo: ticketInfo.previousTicketNo
    });

    ticket.load(ticketInfo, false);
    this.tickets.unshift(ticket);
    this.subscribePusher(ticket);
    this.activeTicket = ticket;
  }

  // </editor-folder>

  end() {
    this.unsubscribePushers();
    // ticketService instance remove
    const initTicketService = new Promise<void>((resolve, reject) => {
      this.tickets.forEach((interaction, i) => {
        interaction.ticketService = null;
        if (i === this.tickets.length - 1) {
          resolve();
        }
      });
    });

    initTicketService.then(() => {
      this.tickets = [];
    });

    this.activeTicket$.next(null);
  }

  // endregion

  unsubscribePusher(channel) {
    console.log('# unsubscribePusher - ', channel);
    this.pusherService.unsubscribe(channel);
  }

  // region lookups
  private async loadCommon() {
    const codes = [
      MasterCode.ServiceType,
      MasterCode.DetailType,
      MasterCode.DealerPortalServiceTypes,
      MasterCode.ProcessType,

    ].join(',');

    const getConstants = await this.apiService.master.getCommonConstantList(codes).toPromise();
    const getStatus = await this.apiService.master.getStatus(Constants.detailType.REPAIR.code).toPromise();
    const dealerOptions = await this.apiService.dealer.getHQDealers().toPromise();

    const { ServiceType, DetailType, DealerPortalServiceTypes, ProcessType } = getConstants;
    this.lookupChanged$.next({
      serviceType: ServiceType,
      dealerPortalServiceType: DealerPortalServiceTypes,
      detailType: DetailType,
      status: getStatus,
      dealerOptions: dealerOptions,
      processTypes: ProcessType,
    });
  }

  private subscribePusher(ticket) {
    // subscribe pusher for editing ticket & events
    let ticketSubscription = null;

    if (ticket?.ticketInfo.status !== 'new') {
      // unsubscribe previous pusher
      if (this.pusherChannels.ticketChannel && ticketSubscription) {
        ticketSubscription.unbind();
        this.pusherService.unsubscribe(this.pusherChannels.ticketChannel);
      }
      this.pusherChannels.ticketChannel = null;
      ticketSubscription = null;

      if (ticket?.ticketInfo?.ticketNo) {
        this.pusherChannels.ticketChannel = `private-ticket-${ ticket.ticketInfo.ticketNo }`;
        this.pusherService.subscribePromise(this.pusherChannels.ticketChannel)
          .then((ticketSub: any) => {
            ticketSubscription = ticketSub;

            ticketSubscription.bind(PusherEvent.TicketChanged, ticketInfo => {
              const { prevTicketNo } = ticketInfo;
              if (prevTicketNo) {
                const find = this.tickets.find(f => f.ticketInfo.ticketNo === prevTicketNo);
                if (find) {
                  find.ticketInfo = ticketInfo;
                  this.subscribePusher(find);
                }
              }
              console.log('ticketInfo =', ticketInfo);
              this.apiService.ticket.getTicket(ticketInfo.ticketNo)
                .pipe(finalize(() => this.spinnerService.stop()))
                .subscribe(res => {
                  ticketInfo = res;
                  ticket.isEdit = false;
                  ticket.pusher(ticketInfo);
                  ticket.ticketChanged$.next(ticketInfo);
                  this.headerTabChanged$.next(ticketInfo);
                });
              // ticket.load(ticketInfo, false);
            });
          });
      }
    }
  }

  private unsubscribePushers() {
    Object.values(this.pusherChannels).forEach(channel => {
      this.pusherService.unsubscribe(channel);
    });
  }

  // endregion
}
